import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ArtifactEntry } from "../stores/artifact-store"

import { FileText, Loader2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { useCanvasStore } from "@/features/canvas/stores/canvas-store"
import { useCreateDocument } from "@/hooks/use-documents"

import { getArtifactRegistryEntry } from "../registry"
import { ArtifactPreview } from "./artifact-preview"
import { ArtifactShareDialog } from "./artifact-share-dialog"
import { useArtifactStore } from "../stores/artifact-store"
import { buildArtifactCanvasSyncPayload, openArtifactCanvas } from "../utils/open-artifact-canvas"
import { openArtifactInPanel } from "../utils/open-artifact-panel"

/** Routes to canvas (markdown) or panel (all other types) based on the registry. */
function openArtifact(target: ArtifactEntry): void {
  if (getArtifactRegistryEntry(target.type).previewMode === "canvas") {
    openArtifactCanvas(target)
  } else {
    openArtifactInPanel(target)
  }
}

/**
 * The content to save or share for this artifact. Artifact canvases open with
 * disablePersistence, so a user's canvas edits are never written back to the
 * artifact store: they exist only in the canvas store (the editor writes each
 * change there as markdown). Use them while the canvas still shows this artifact
 * with edits, otherwise the artifact's latest version.
 */
function currentArtifactContent(identifier: string, latestContent: string): string {
  const canvas = useCanvasStore.getState()
  if (canvas.messageId === identifier && canvas.disablePersistence && canvas.isDirty) {
    return canvas.content
  }
  return latestContent
}

// Sandbox-hosted types render in an iframe that fills its container, so the
// inline block below must give them a real height; chart/svg/mermaid/sheet
// size themselves.
const IFRAME_ARTIFACT_TYPES = new Set(["html", "react", "slides"])

export function ArtifactCard({
  identifier,
  inlinePreview = false,
}: {
  identifier: string
  /** #3024 (hosted-agent chat): render the finished artifact directly under
   *  the card instead of auto-opening the side panel — the agent "sends a
   *  chart into its chat window". Explorer keeps the card+panel behavior. */
  inlinePreview?: boolean
}) {
  const { t } = useTranslation()
  // artifacts is Record<string, ArtifactEntry>; cast so TypeScript knows the
  // key may be absent and null-checks below are not flagged as unnecessary.
  const entry = useArtifactStore((s) => s.artifacts[identifier] as ArtifactEntry | undefined)
  const openArtifactId = useArtifactStore((s) => s.openArtifactId)
  const markArtifactShared = useArtifactStore((s) => s.markArtifactShared)
  const createDocument = useCreateDocument()
  const [shareOpen, setShareOpen] = useState(false)
  const [shareDocId, setShareDocId] = useState<string | null>(null)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const autoOpenedRef = useRef(false)

  const latestContent = entry?.versions.at(-1)?.content ?? ""

  // Auto-open the canvas or panel the first time this artifact starts streaming.
  // Routing is per type: markdown → canvas, all other types → panel.
  useEffect(() => {
    // Inline mode renders in place — yanking the side panel open on top of the
    // conversation is exactly what it exists to avoid.
    if (inlinePreview) return
    if (autoOpenedRef.current || !entry) return
    if (entry.status === "streaming") {
      autoOpenedRef.current = true
      openArtifact(entry)
    }
  }, [entry, inlinePreview])

  // Live-sync canvas content while streaming — only for markdown (previewMode === "canvas").
  // The panel subscribes to the artifact store directly and needs no sync.
  // Never sync over the user's unsaved edits.
  // Accepted tradeoff: during streaming the whole canvas doc is replaced on each tick
  // (cursor/selection loss is accepted while status is "streaming"); the isDirty guard
  // is the designed protection for user edits; a dedicated lighter store action is
  // planned as a follow-up (Fas 2).
  // We call openCanvas() directly (not openCanvasInSidebar) to bump loadRevision so
  // the canvas pane's effect picks up the new content without reopening the sidebar.
  //
  // The sync must also run ONCE when streaming ends: the store rebuilds the final
  // content when the closing tag arrives, and without a final sync the canvas would
  // keep the last mid-stream state (truncated tail / partial closing tag).
  //
  // openArtifactId alone is not enough: closing the canvas sidebar (or the artifact
  // panel's onClose for other types) only clears the canvas/panel state, not this
  // artifact store flag — so a stale openArtifactId can still match after the user
  // has closed the canvas and opened something else entirely (e.g. a saved document
  // from the library). Guard against that by also requiring the canvas store's own
  // messageId to still point at this artifact; openArtifactCanvas sets messageId to
  // the artifact identifier, and closing/repointing the canvas (clearCanvas, or
  // opening a document/another artifact) changes or clears it.
  const wasStreamingRef = useRef(false)
  useEffect(() => {
    if (!entry || openArtifactId !== identifier) return
    // Canvas live-sync only applies to markdown artifacts (previewMode === "canvas").
    // Panel-rendered types subscribe to the store themselves.
    if (getArtifactRegistryEntry(entry.type).previewMode !== "canvas") return
    const isStreamingNow = entry.status === "streaming"
    const justFinishedStreaming = wasStreamingRef.current && !isStreamingNow
    wasStreamingRef.current = isStreamingNow
    const canvasStore = useCanvasStore.getState()
    if (canvasStore.messageId !== identifier) return
    if ((isStreamingNow || justFinishedStreaming) && !canvasStore.isDirty) {
      canvasStore.openCanvas(buildArtifactCanvasSyncPayload(entry, latestContent))
    }
  }, [entry, identifier, latestContent, openArtifactId])

  if (!entry) return null

  let statusKey: string | null = null
  if (entry.status === "streaming") {
    statusKey = "artifacts.status_streaming"
  } else if (entry.status === "incomplete") {
    statusKey = "artifacts.status_incomplete"
  } else if (entry.status === "error") {
    statusKey = "artifacts.status_error"
  }

  // Persist the session image context so sandbox:/mnt/data/… refs in saved
  // html/react/slides artifacts still resolve when reopened from the library
  // (openSavedArtifact restores these).
  const buildDocPayload = () => ({
    title: entry.title,
    content: currentArtifactContent(entry.identifier, latestContent),
    artifactType: entry.type,
    ...(entry.sessionId ? { sourceSessionId: entry.sessionId } : {}),
    ...(entry.sessionFilenames && entry.sessionFilenames.length > 0
      ? { artifactMeta: { sessionFilenames: entry.sessionFilenames } }
      : {}),
  })

  const saveAsDocument = () =>
    createDocument.mutate(buildDocPayload(), {
      onSuccess: (created) => {
        // Remember the document so a later Share reuses it instead of creating a
        // second, disconnected copy. Key by the artifact's own session.
        markArtifactShared(entry.identifier, created.id, entry.sessionId)
        toast.success(t("artifacts.saved_as_document"))
      },
      onError: () => toast.error(t("artifacts.save_failed")),
    })

  const openShare = () => {
    // Already document-backed (saved/shared earlier this session, or opened from
    // the library) → reuse that document rather than orphaning invited participants.
    if (entry.imageSource?.kind === "document") {
      setShareDocId(entry.imageSource.documentId)
      setShareOpen(true)
      return
    }
    createDocument.mutate(buildDocPayload(), {
      onSuccess: (created) => {
        markArtifactShared(entry.identifier, created.id, entry.sessionId)
        setShareDocId(created.id)
        setShareOpen(true)
      },
      onError: () => toast.error(t("artifacts.save_failed")),
    })
  }

  const showInlinePreview =
    inlinePreview &&
    entry.status === "complete" &&
    Boolean(latestContent) &&
    getArtifactRegistryEntry(entry.type).previewMode === "panel"

  return (
    <span className="not-prose my-2 block">
      {/* Wraps on narrow screens (QA 2026-09-13, mobile): the title takes a
          minimum basis so the three actions drop to their own line instead of
          being crushed in the chat column. */}
      <span className="border-primary/40 bg-primary/5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border p-3">
        {entry.status === "streaming" ? (
          <Loader2 className="text-primary size-5 shrink-0 animate-spin" />
        ) : (
          <FileText className="text-primary size-5 shrink-0" />
        )}
        <span className="min-w-0 flex-1 basis-40">
          <span className="block truncate font-medium">{entry.title}</span>
          <span className="text-muted-foreground block text-xs">
            {t("artifacts.version_count", { count: entry.versions.length })}
            {statusKey ? ` · ${t(statusKey)}` : ""}
          </span>
        </span>
        <span className="flex shrink-0 flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => openArtifact(entry)}>
            {t("artifacts.open")}
          </Button>
          <Button
            disabled={!latestContent || createDocument.isPending}
            size="sm"
            variant="ghost"
            onClick={saveAsDocument}
          >
            {t("artifacts.save_as_document")}
          </Button>
          <Button
            disabled={!latestContent || entry.status === "streaming" || createDocument.isPending}
            size="sm"
            variant="ghost"
            onClick={openShare}
          >
            {t("artifacts.share", { defaultValue: "Share" })}
          </Button>
        </span>
        {shareOpen && shareDocId ? (
          <ArtifactShareDialog
            documentId={shareDocId}
            open={shareOpen}
            onOpenChange={setShareOpen}
          />
        ) : null}
      </span>
      {showInlinePreview ? (
        <span
          data-testid="artifact-inline-preview"
          className={`bg-card mt-2 block overflow-hidden rounded-lg border ${
            IFRAME_ARTIFACT_TYPES.has(entry.type) ? "h-96" : ""
          }`}
        >
          <ArtifactPreview content={latestContent} entry={entry} onRuntimeError={setInlineError} />
        </span>
      ) : null}
      {inlinePreview && inlineError ? (
        <span className="text-destructive mt-1 block text-xs">{inlineError}</span>
      ) : null}
    </span>
  )
}
